<script setup lang="ts">
import DeviceCard from '@/components/DeviceComponents/Card/DeviceCard.vue'
import { GridLayout, StackLayout } from '@/components/Layouts'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import FullScreenLoader from '@/components/LoadingSpinner/FullScreenLoader.vue'
import { useGridSize } from '@/hooks/useGridSize'
import { EGridSize } from '@/types/enum/ViewsUtilsEnum'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { AlertSocketMessageModel } from '@/types/socket/SocketAlertModel'
import type { SocketDeviceDetectionModel } from '@/types/socket/SocketDetectionModel'
import { addToQuery } from '@/utils/queryUtils'
import { isMobile, isMobileEvent } from '@/utils/viewsUtils'
import { nextTick, provide, ref } from 'vue'
import { useRoute } from 'vue-router'

type Props = {
    data: DeviceModel[]
    selectedDevice?: Partial<DeviceModel>
    statuses: AlertSocketMessageModel[]
    detections?: SocketDeviceDetectionModel[]
    loading?: boolean
    showNextEvent?: boolean
    showNextCheckpoint?: boolean
    disableClick?: boolean
}
defineProps<Props>()

const route = useRoute()

const gridSize = useGridSize({
    smallscreen: EGridSize.IS_MOBILE,
    mobile: EGridSize.IS_MOBILE,
    tablet: EGridSize.IS_TABLET - 1,
    desktop: EGridSize.IS_DESKTOP - 1,
    widescreen: EGridSize.IS_WIDESCREEN - 1
})

const getDeviceStatus = (device: Partial<DeviceModel>) => ({
    battery: device.battery,
    plugged: device.plugged,
    signal: device.signal,
    network_type: device.network_type
})

const handleSelectDevice = async (device?: DeviceModel) => {
    await nextTick(() => addToQuery({ preview: device?.device_id ?? device?.id }, true))
}

const isMobileLayout = ref(isMobile())
isMobileEvent(e => (isMobileLayout.value = e.matches))

provide('isResponsiveTablet', ref(true))
provide('isResponsiveDesktop', ref(true))
provide('noAddToQuery', ref(true))
</script>

<template>
    <GridLayout
        :maxItemPerRow="isMobileLayout || !selectedDevice ? gridSize : undefined"
        :gap="8"
        class="relative full-width grid-outlook"
        v-bind="$attrs">
        <FullScreenLoader :isLoading="!!loading" />
        <DeviceCard
            v-for="(device, key) in data"
            :key="key"
            :data="device"
            :status="{
                status: parseInt(device.status as string),
                ...getDeviceStatus(device),
                ...(statuses?.find(item => item.device_id === (device.device_id ?? device.id)) ??
                    {})
            }"
            :detections="
                detections?.find(item => item.device_id === (device.device_id ?? device.id))
                    ?.detections ?? []
            "
            :disableClick="disableClick"
            :showNextEvent="showNextEvent"
            :showNextCheckpoint="showNextCheckpoint"
            @click="disableClick ? undefined : handleSelectDevice(device)"
            :class="`full-width ${
                (route.query.preview
                    ? parseInt(route.query.preview?.toString())
                    : selectedDevice?.device_id ?? selectedDevice?.id) ===
                (device.device_id ?? device.id)
                    ? 'selected-device'
                    : undefined
            }`">
            <template #iconHeader>
                <slot name="iconHeader" v-bind="device" />
            </template>
            <template #content>
                <slot name="cardContent" v-bind="device" />
            </template>
        </DeviceCard>
    </GridLayout>

    <StackLayout v-if="!data?.length" class="align-self-center no-record">
        <LoadingSpinner :isLoading="!!loading" />
        {{ $t('common.noRecord') }}
    </StackLayout>
</template>

<style scoped lang="scss">
.selected-device {
    border-width: 4px;
    background-color: var(--color-background-primary);
}
</style>
